<template>
	<BaseModal
		ref="baseModalRef"
		:header-title="headerTitle"
		:full-screen="true"
		:without-padding="true"
		class="gallery-modal-new"
		@close="handleStopVideo"
	>
		<div class="gallery-modal-new__content">
			<div
				ref="swiperRef"
				class="gallery-modal-new__swiper-container"
			>
				<swiper-container
					v-bind="swiperSettings"
					class="gallery-modal-new__swiper"
					@swiperslidechange="handleSlideChange"
				>
					<swiper-slide
						v-for="(element, index) in elements"
						:key="index"
						class="swiper-slide"
					>
						<div class="gallery-modal-new__slide-content">
							<template v-if="element.type === 'image'">
								<div
									class="gallery-modal-new__image-container"
									:class="[
										getLandscapePortraitClass(element),
										{ 'gallery-modal-new__image-container--loading': !loadedImages[index] },
									]"
								>
									<img
										:class="['gallery-modal-new__image', { 'gallery-modal-new__image--loaded': loadedImages[index] }]"
										:src="element.content"
										:data-order="index"
										:alt="element.alt || `Image ${index + 1}`"
										:title="formatCopyrightString(element?.copyright)"
										@load="(e) => handleImageLoaded(e, index)"
									/>
									<div
										v-if="!loadedImages[index]"
										class="gallery-modal-new__image-placeholder"
									></div>
								</div>
							</template>
							<template v-else>
								<div class="gallery-modal-new__video-wrapper">
									<embed
										class="gallery-modal-new__video"
										:src="getVideoSrc(element)"
										tabindex="-1"
										:title="`Video ${index + 1}`"
									/>
								</div>
							</template>
						</div>
					</swiper-slide>
				</swiper-container>

				<div class="gallery-modal-new__navigation">
					<div
						:id="`${uniqueId}-prev`"
						class="gallery-modal-new__prev"
					>
						<FaIcon :icon="['fa-solid', 'fa-chevron-left']" />
					</div>
					<div
						:id="`${uniqueId}-next`"
						class="gallery-modal-new__next"
					>
						<FaIcon :icon="['fa-solid', 'fa-chevron-right']" />
					</div>
				</div>

				<div
					:id="`${uniqueId}-pagination`"
					class="swiper-pagination"
				></div>
			</div>
		</div>
	</BaseModal>
</template>

<script lang="ts" setup>
import BaseModal from '@lmt-rpb/BaseModal/BaseModal.vue';
import { ref, nextTick, watch, reactive, onBeforeUnmount } from 'vue';
import type { GalleryElement } from '@/interfaces/components/galleryElement';
import { v4 as uuidv4 } from 'uuid';
import { register } from 'swiper/element/bundle';

import 'swiper/css';
import 'swiper/css/pagination';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import type { SwiperOptions } from 'swiper/types';

register();

interface Props {
	elements: GalleryElement[];
	initialIndex?: number;
	headerTitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
	initialIndex: 0,
	headerTitle: '',
});

const baseModalRef = ref<InstanceType<typeof BaseModal> | null>(null);
const swiperRef = ref<HTMLElement | null>(null);
const activeIndex = ref(props.initialIndex);
const loadedImages = reactive<Record<number, boolean>>({});
const uniqueId = 'id-' + uuidv4();

const swiperSettings: SwiperOptions = {
	slidesPerView: 1,
	centeredSlides: true,
	initialSlide: props.initialIndex,
	speed: 400,
	resistance: true,
	resistanceRatio: 0.85,
	touchRatio: 1,
	threshold: 5,
	preventInteractionOnTransition: true,
	keyboard: {
		enabled: true,
		onlyInViewport: true,
		pageUpDown: false,
	},
	navigation: {
		nextEl: `#${uniqueId}-next`,
		prevEl: `#${uniqueId}-prev`,
		hiddenClass: 'swiper-button-hidden',
		disabledClass: 'swiper-button-disabled',
	},
	pagination: {
		el: `#${uniqueId}-pagination`,
		dynamicBullets: true,
	},
};

const getVideoSrc = (element: GalleryElement) => {
	if (element.type === 'youtube' || element.type === 'ytube') {
		return `https://www.youtube.com/embed/${element.content}?rel=0`;
	}
	if (element.type === 'vimeo') {
		return `https://player.vimeo.com/video/${element.content}`;
	}
	return element.content;
};

const handleSlideChange = (swiperEvent: CustomEvent) => {
	if (!swiperEvent?.detail[0]) return;

	const swiperInstance = swiperEvent.detail[0];
	activeIndex.value = swiperInstance.activeIndex;

	updateNavigationVisibility(swiperInstance);
};

interface SwiperInstance {
	isBeginning: boolean;
	isEnd: boolean;
	activeIndex: number;
	destroy?: (deleteInstance?: boolean, cleanStyles?: boolean) => void;
}

const updateNavigationVisibility = (swiper: SwiperInstance) => {
	const prevEl = swiperRef.value?.querySelector('.gallery-modal-new__prev');
	const nextEl = swiperRef.value?.querySelector('.gallery-modal-new__next');

	if (prevEl) {
		if (swiper.isBeginning) {
			prevEl.classList.add('swiper-button-hidden');
		} else {
			prevEl.classList.remove('swiper-button-hidden');
		}
	}

	if (nextEl) {
		if (swiper.isEnd) {
			nextEl.classList.add('swiper-button-hidden');
		} else {
			nextEl.classList.remove('swiper-button-hidden');
		}
	}
};

const openModal = () => {
	if (baseModalRef.value) {
		baseModalRef.value.openModal();

		nextTick(() => {
			if (swiperRef.value) {
				const swiperEl = swiperRef.value.querySelector('swiper-container');
				if (swiperEl && swiperEl.swiper) {
					swiperEl.swiper.update();
					swiperEl.swiper.slideTo(props.initialIndex, 0);
					updateNavigationVisibility(swiperEl.swiper);
				}
			}
		});
	}
};

const clearSwiper = () => {
	if (swiperRef.value) {
		const swiperEl = swiperRef.value.querySelector('swiper-container');
		if (swiperEl) {
			swiperEl.swiper?.destroy?.(true, true);
		}
	}
};

onBeforeUnmount(() => {
	clearSwiper();
});

const initializeLoadingStates = () => {
	nextTick(() => {
		const currentIndex = activeIndex.value;

		prepareImageLoading(currentIndex); // Current slide
		prepareImageLoading(currentIndex + 1); // Next slide
		prepareImageLoading(currentIndex - 1); // Previous slide
	});
};

const initializeAdjacentLoadingStates = (currentIndex: number) => {
	// Preload next 2 slides
	prepareImageLoading(currentIndex + 1);
	prepareImageLoading(currentIndex + 2);

	// Preload previous 2 slides
	prepareImageLoading(currentIndex - 1);
	prepareImageLoading(currentIndex - 2);
};

const prepareImageLoading = (index: number) => {
	if (index >= 0 && index < props.elements.length && !loadedImages[index]) {
		loadedImages[index] = false;
	}
};

const handleImageLoaded = (event: Event, index: number) => {
	if (event.target) {
		loadedImages[index] = true;
	}
};

const getLandscapePortraitClass = (element: GalleryElement) => {
	// Default to landscape for non-image types
	if (element.type !== 'image') {
		return 'gallery-modal-new__image-container--landscape';
	}

	const img = document.querySelector(`img[src="${element.content}"]`) as HTMLImageElement;
	if (img && img.naturalWidth && img.naturalHeight) {
		const ratio = img.naturalWidth / img.naturalHeight;
		if (ratio < 0.95) {
			return 'gallery-modal-new__image-container--portrait';
		}
		if (ratio >= 0.95 && ratio <= 1.05) {
			return 'gallery-modal-new__image-container--square';
		}
	}
	return 'gallery-modal-new__image-container--landscape';
};

watch(activeIndex, async (newIndex, oldIndex) => {
	await nextTick(() => {
		handleStopVideo(oldIndex);
		initializeAdjacentLoadingStates(newIndex);
	});
});

const formatCopyrightString = (text?: string) => {
	if (!text) {
		return `© GIATA GmbH 1996 - ${new Date().getFullYear()}`;
	}
	if (text.includes('©')) {
		return text;
	}
	if (text.includes('&copy;')) {
		return text.replace('&copy;', '©');
	}
	return `© ${text}`;
};

nextTick(() => {
	initializeLoadingStates();
});

function handleStopVideo(oldIndex?: number) {
	const slideIndex = oldIndex ?? activeIndex.value;
	const element = props.elements[slideIndex];
	const slideElement = swiperRef.value?.querySelector(`.swiper-slide:nth-child(${slideIndex + 1})`);
	if (slideElement) {
		const videoEmbed = slideElement.querySelector('.gallery-modal-new__video') as HTMLEmbedElement;
		if (videoEmbed) {
			videoEmbed.src = getVideoSrc(element);
		}
	}
}

defineExpose({ openModal });
</script>

<style lang="scss" scoped>
.gallery-modal-new {
	&__content {
		height: 100%;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__swiper-container {
		width: 100%;
		height: 100%;
		position: relative;
	}

	&__swiper {
		width: 100%;
		height: 100%;
		position: relative;
	}

	&__slide-content {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		padding: 6rem 0;
		z-index: 1;
	}

	&__image-container {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 20rem;
		padding: 0;
		transition: all 0.3s ease;
		width: 100%;
		margin: 0 auto;

		&--landscape {
			width: 100%;
			max-width: 100%;
			height: auto;
			padding: 0;
		}

		&--portrait {
			width: 100%;
			max-width: 100%;
			height: 100%;
		}

		&--square {
			width: 100%;
			height: auto;
		}

		&--loading {
			min-height: 200px;
			opacity: 0.9;
		}
	}

	&__image {
		max-width: 100%;
		max-height: 100%;
		opacity: 0;
		transition: opacity 0.3s ease-in;
		border-radius: 0;
		object-fit: contain;
		display: block;

		.gallery-modal-new__image-container--landscape & {
			width: 100%;
			height: auto;
		}

		.gallery-modal-new__image-container--portrait & {
			width: auto;
			height: 100%;
		}

		.gallery-modal-new__image-container--square & {
			width: 100%;
			height: 100%;
		}

		&--loaded {
			opacity: 1;
		}
	}

	&__image-placeholder {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 100%;
		background-color: rgb(215 215 215 / 50%);
		border-radius: inherit;
		overflow: hidden;
		z-index: 1;
		transition: opacity 0.3s ease;
		padding-bottom: 100%;

		.gallery-modal-new__image-container--landscape & {
			width: 100%;
		}

		.gallery-modal-new__image-container--portrait & {
			width: auto;
			height: 100%;
			aspect-ratio: 0.75;
		}

		.gallery-modal-new__image-container--square & {
			width: 100%;
		}

		&::before {
			content: '';
			display: block;
			position: absolute;
			inset: 0;
			background: linear-gradient(
				90deg,
				transparent,
				transparent,
				var(--gradient-color, $color-stars-background),
				transparent,
				transparent
			);
			transform: translateX(-100%);
			animation: wave 1.5s linear infinite;
		}
	}

	&__video-wrapper {
		width: 100%;
		height: 0;
		padding-bottom: 70%;
		position: relative;
		max-width: 100%;
		margin: 0 auto;
		margin-bottom: 5rem;
		z-index: 2;

		@media (min-width: $breakpoint-mobiletabs) {
			padding-bottom: 40%;
			margin: 3rem;
			margin-bottom: 5rem;
			max-width: calc(100% - 10rem);
		}

		@media (min-width: $breakpoint-small) {
			margin: 8rem;
			margin-bottom: 8rem;
			max-width: calc(100% - 16rem);
		}
	}

	&__video {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		transform: none;
		margin: 0;
		z-index: 3;
	}

	&__copyright {
		position: absolute;
		right: 0;
		bottom: 1rem;
		padding: 0.5rem 1rem;
		background: $color-white;
		font-size: $font-small-3;
		color: $color-dark-gray;
	}

	&__navigation {
		z-index: 4;
	}

	&__prev,
	&__next {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		z-index: 1;
		margin: 0;
		border-radius: 0;
		display: none;
		cursor: pointer;
		color: $color-primary;

		& > i {
			font-size: 3.6rem;
		}

		&::after {
			display: none;
		}

		&.swiper-button-disabled {
			opacity: 0;
		}

		&.swiper-button-hidden {
			opacity: 0;
			visibility: hidden;
		}
	}

	&__prev {
		left: 3.2rem;
	}

	&__next {
		right: 3.2rem;
	}

	.swiper-pagination {
		bottom: 2.5rem;
	}
}

:deep(.swiper-pagination-bullet-active) {
	background: $color-black;
}

:deep(.swiper-pagination-bullet) {
	height: 1rem;
	width: 1rem;
}

:deep(.swiper-pagination-bullet-active-next) {
	background: $color-placeholder-text;
	transform: scale(1);
	width: 0.8rem;
	height: 0.8rem;
}

:deep(.swiper-pagination-bullet-active-prev) {
	width: 0.8rem;
	height: 0.8rem;
	background: $color-placeholder-text;
	transform: scale(1);
}

:deep(.swiper-pagination-bullet-active-next-next) {
	transform: scale(0.6);
	background: $color-placeholder-text;
}

:deep(.swiper-pagination-bullet-active-prev-prev) {
	transform: scale(0.6);
	background: $color-placeholder-text;
}

@media (min-width: $breakpoint-small) {
	.gallery-modal-new {
		&__image-container {
			min-height: 28rem;

			&--landscape {
				padding: 6rem 0;
				height: calc(100vh - 6rem);
			}
		}

		.gallery-modal-new__image-container--square & {
			width: calc(100% - 8rem);
			padding-bottom: calc(100% - 8rem);
		}
	}
}

@media (min-width: $breakpoint-scroller) {
	.gallery-modal-new {
		.swiper-pagination {
			display: none;
		}

		&__image {
			padding: 0 6rem;
		}

		&__image-container {
			min-height: 30rem;
			box-sizing: border-box;
			height: 100%;

			&--landscape {
				padding: 0;
				display: flex;
				align-items: center;
				justify-content: center;
			}

			&--portrait {
				width: auto;
				max-width: 80%;
				display: flex;
				align-items: center;
				justify-content: center;
			}

			&--square {
				display: flex;
				align-items: center;
				justify-content: center;
			}
		}

		&__image {
			border-radius: $border-radius-small;
		}

		&__image-placeholder {
			border-radius: $border-radius-small;
			width: calc(100% - 12rem);
			padding: 0;

			.gallery-modal-new__image-container--landscape & {
				height: 80%;
				width: 80%;
			}

			.gallery-modal-new__image-container--portrait & {
				width: auto;
				height: calc(100% - 12rem);
				aspect-ratio: 0.75;
			}

			.gallery-modal-new__image-container--square & {
				width: min(80vh, calc(100% - 12rem));
				padding-bottom: min(80vh, calc(100% - 12rem));
			}
		}

		&__video-wrapper {
			width: 100vw;
			height: 0;
			padding-bottom: calc(min(80vw, calc(100vh - 16rem)));
			position: relative;
			max-width: 120rem;
			margin: 6rem;
		}

		&__video {
			border-radius: $border-radius-small;
		}

		&__prev,
		&__next {
			display: block;
			width: 2.2rem;
			height: 3.8rem;
		}
	}
}

@media (min-width: $breakpoint-verylarge) {
	.gallery-modal-new {
		&__image {
			padding: 0 10rem;
		}

		&__prev {
			left: 5rem;
		}

		&__next {
			right: 5rem;
		}
	}
}

@keyframes wave {
	0% {
		transform: translateX(-100%);
	}

	100% {
		transform: translateX(100%);
	}
}
</style>
